import { cx } from "@suse-o11y/class-variance";
import { IconReloadOutlined } from "@suse-o11y/icons";
import { useHandler } from "@suse-o11y/use-handler";
import { useInterval } from "@suse-o11y/use-interval";
import React from "react";

import { Select } from "~/components/select";
import { SelectContent } from "~/components/select/content";
import { SelectItem } from "~/components/select/item";
import { SelectTrigger } from "~/components/select/trigger";

import { LOADING_DURATION_MS, useFullLoadingAnimation } from "./_use-full-loading-animation";
import { REFRESH_INTERVALS, type RefreshInterval, refreshIntervalToMs, toRefreshInterval } from "./utils";

export interface RefreshIntervalSelectorProps {
	className?: string;
	loading?: boolean;
	disabled?: boolean;
	value: RefreshInterval;
	onChange: (value: RefreshInterval) => void;
	onRefresh: VoidFunction;
}

export const RefreshIntervalSelector = React.forwardRef<React.ComponentRef<"div">, RefreshIntervalSelectorProps>(
	({ className, loading = false, disabled, value, onChange, onRefresh, ...props }, ref) => {
		const loadingFull = useFullLoadingAnimation(loading);
		const stableRefresh = useHandler(() => {
			if (!loading) {
				onRefresh();
			}
		});

		useInterval(
			() => {
				stableRefresh();
			},
			[stableRefresh],
			refreshIntervalToMs(value) ?? Number.POSITIVE_INFINITY,
			{
				pause: value === "off",
			},
		);

		return (
			<div className={cx("join", className)} ref={ref} {...props}>
				<button
					className="join-item button button-tertiary state-loading:cursor-wait gap-2"
					data-state={loading ? "loading" : undefined}
					onClick={stableRefresh}
					type="button"
				>
					<IconReloadOutlined
						className={cx("text-xs/0", loadingFull && "animate-spin")}
						style={{ animationDuration: `${LOADING_DURATION_MS}ms` }}
					/>
					Refresh
				</button>

				<div className="join-item">
					<Select
						disabled={disabled}
						onValueChange={(interval) => onChange(toRefreshInterval(interval) ?? "off")}
						value={value}
					>
						<SelectTrigger className="gap-2">{value !== "off" && value}</SelectTrigger>

						<SelectContent align="end" className="z-drawer! min-w-16" width="auto">
							{REFRESH_INTERVALS.map((interval) => (
								<SelectItem className="justify-center" key={interval} value={interval}>
									{interval === "off" ? "Off" : interval}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
		);
	},
);
